import React, { useEffect, useState, useCallback, Fragment } from 'react';
import apiClient from '../api/axios';
import { Container, Row, Col, Card, Table, Spinner, Alert, Dropdown, ListGroup } from 'react-bootstrap'; // Added Dropdown
import { Link } from 'react-router-dom';
import { formatUtcToLocal } from '../utils/dateUtils';
import { toast } from 'react-toastify';

import ConfirmationModal from '../components/ConfirmationModal'; // New import
import { useAuth } from '../contexts/AuthContext'; // Import useAuth

// Custom toggle component for the dropdown to use an icon
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="text-dark p-2"
      style={{ background: 'none', border: 'none' }}
    >
      {children}
    </button>
));

const AdminDashboard = () => {
    const { user: currentUser } = useAuth(); // Get current user
    const [activeUsers, setActiveUsers] = useState([]);
    const [diskUsage, setDiskUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null); // Clear previous errors

        try {
            const activeUsersResponse = await apiClient.get('/admin/users/active');
            setActiveUsers(activeUsersResponse.data);

            const diskUsageResponse = await apiClient.get('/admin/disk-usage/');
            setDiskUsage(diskUsageResponse.data);

            setLoading(false);
        } catch (err) {
            setError('Не удалось загрузить данные администратора.');
            setLoading(false);
            console.error(err);
        }
    }, []); // Dependencies are empty as it fetches global admin data

    useEffect(() => {
        fetchData(); // Initial fetch

        const activeUsersInterval = setInterval(() => {
            // Fetch active users separately for more frequent updates
            const fetchActiveUsers = async () => {
                try {
                    const response = await apiClient.get('/admin/users/active');
                    setActiveUsers(response.data);
                } catch (err) {
                    console.error("Failed to fetch active users:", err);
                }
            };
            fetchActiveUsers();
        }, 30000); // Every 30 seconds

        const diskUsageInterval = setInterval(() => {
            // Fetch disk usage separately for less frequent updates
            const fetchDiskUsage = async () => {
                try {
                    const response = await apiClient.get('/admin/disk-usage/');
                    setDiskUsage(response.data);
                } catch (err) {
                    console.error("Failed to fetch disk usage:", err);
                }
            };
            fetchDiskUsage();
        }, 60000); // Every 60 seconds

        return () => {
            clearInterval(activeUsersInterval);
            clearInterval(diskUsageInterval);
        };
    }, [fetchData]);


    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Fragment>
        <Container className="mt-5">
            <h1 className="mb-4">Панель администратора</h1>

            <Row>
                <Col md={12} className="mb-4">
                    <Card>
                        <Card.Header>
                            <i className="fas fa-cogs me-2"></i>Разделы Администрирования
                        </Card.Header>
                        <ListGroup variant="flush">
                            <ListGroup.Item action as={Link} to="/admin/organizations">
                                <i className="fas fa-building me-2"></i>Организации
                            </ListGroup.Item>
                            <ListGroup.Item action as={Link} to="/admin/users">
                                <i className="fas fa-users me-2"></i>Пользователи
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12} className="mb-4">
                    <Card>
                        <Card.Header>
                            <i className="fas fa-hdd me-2"></i>Использование диска (/uploads)
                        </Card.Header>
                        <Card.Body>
                            {diskUsage ? (
                                <Row>
                                    <Col>
                                        <p className="mb-1"><strong>Всего:</strong> {formatBytes(diskUsage.total)}</p>
                                        <p className="mb-1"><strong>Использовано приложением:</strong> {formatBytes(diskUsage.used)}</p>
                                        <p className="mb-1"><strong>Использовано другими файлами:</strong> {formatBytes(diskUsage.used_by_other)}</p>
                                        <p className="mb-1"><strong>Зарезервировано:</strong> {formatBytes(diskUsage.reserved)}</p>
                                        <p className="mb-0"><strong>Свободно:</strong> {formatBytes(diskUsage.free)}</p>
                                    </Col>
                                </Row>
                            ) : (
                                <p>Загрузка...</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12} className="mb-4">
                    <Card>
                        <Card.Header>
                            <i className="fas fa-wifi me-2"></i>Активные пользователи ({activeUsers.length})
                        </Card.Header>
                        <Card.Body>
                            {activeUsers.length > 0 ? (
                                <ListGroup variant="flush">
                                    {activeUsers.map(user => (
                                        <ListGroup.Item key={user.id} className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>{user.name ? user.name : "Имя не указано"} ({user.email})</strong>
                                                <small className="d-block text-muted">
                                                    {user.organization ?
                                                        <a href={"/admin/organizations/"+user.organization.id}>{user.organization.name}</a>
                                                    : 'Без организации'}
                                                </small>
                                            </div>
                                            <small className="text-muted">
                                                Был активен: {formatUtcToLocal(user.last_active_at)}
                                            </small>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="text-muted mb-0">Сейчас нет активных пользователей.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            

            
        </Container>

        

        
        

        

        
        </Fragment>
    );
};

export default AdminDashboard;
